import { MASTERY_CATEGORIES, masteryLevel } from "../service/mastery";
import { MasteryLevel } from "../types/enums";
import type { MasteryTimelinePoint } from "../types/standards";

// Abstract SVG units, not CSS pixels — the wrapper below scales this to the
// container's actual width via aspect-ratio, so a 340x150 viewBox stays
// legible whether the drawer renders at 420px or 92vw on a small screen.
export const TIMELINE_CHART_VIEW_W = 340;
export const TIMELINE_CHART_VIEW_H = 150;

const MARGIN = { top: 10, right: 8, bottom: 22, left: 22 };
const PLOT_W = TIMELINE_CHART_VIEW_W - MARGIN.left - MARGIN.right;
const PLOT_H = TIMELINE_CHART_VIEW_H - MARGIN.top - MARGIN.bottom;

// Horizontal reference bands, derived by sampling masteryLevel itself (at
// 0.5/0.8 today) rather than re-declaring its thresholds here, so the chart
// can't silently drift out of sync with the mastery-level pill above it.
// masteryLevel(v, true) never returns NOT_STARTED, so "Not started" has no
// footprint on a continuous 0..1 mastery axis — only the three "started"
// categories get a real band.
const BAND_RESOLUTION = 200;
const MASTERY_BANDS: { level: MasteryLevel; from: number; to: number }[] =
  (() => {
    const bands: { level: MasteryLevel; from: number; to: number }[] = [];
    let current: MasteryLevel | null = null;
    let start = 0;
    for (let i = 0; i <= BAND_RESOLUTION; i++) {
      const v = i / BAND_RESOLUTION;
      const lvl = masteryLevel(v, true);
      if (lvl !== current) {
        if (current !== null)
          bands.push({ level: current, from: start, to: v });
        current = lvl;
        start = v;
      }
    }
    if (current !== null) bands.push({ level: current, from: start, to: 1 });
    return bands;
  })();

function parseLocalDate(dateStr: string): Date {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1);
}

function formatTick(date: Date): string {
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

interface MasteryTimelineChartProps {
  points: MasteryTimelinePoint[];
}

/**
 * Render a stepped mastery chart for at least two dated points.
 *
 * Stepped, not interpolated: mastery holds flat between measurement days. One
 * point degenerates to a zero-width line, so the caller handles that as text.
 */
const MasteryTimelineChart = ({ points }: MasteryTimelineChartProps) => {
  const dates = points.map((p) => parseLocalDate(p.date).getTime());
  const minT = Math.min(...dates);
  const maxT = Math.max(...dates);
  const span = maxT - minT || 1;

  const xOf = (t: number) => MARGIN.left + ((t - minT) / span) * PLOT_W;
  const yOf = (m: number) =>
    MARGIN.top + (1 - Math.min(1, Math.max(0, m))) * PLOT_H;

  const xs = dates.map(xOf);
  const ys = points.map((p) => yOf(p.mastery));

  let path = `M ${xs[0]} ${ys[0]}`;
  for (let i = 1; i < points.length; i++) {
    // Hold the previous value until this point's date, then jump.
    path += ` L ${xs[i]} ${ys[i - 1]} L ${xs[i]} ${ys[i]}`;
  }

  // Up to 4 ticks spread evenly across the date span.
  //
  // The outermost two carry their own anchor. A centred "Sep 14" is ~34 units
  // wide at fontSize 10, so the last tick — which sits at the plot's right edge,
  // MARGIN.right (8) from the viewBox — would hang ~9 units past it and get
  // clipped to "Sep 1". Anchoring the edges inward costs nothing: a label whose
  // end aligns with the axis's end still reads as belonging to it, whereas
  // widening MARGIN.right would shrink the plot and still break on a wider
  // label. Interior ticks stay centred on their mark.
  const tickCount = Math.min(4, points.length);
  const ticks = Array.from({ length: tickCount }, (_, i) => {
    const t = tickCount === 1 ? minT : minT + (i / (tickCount - 1)) * span;
    const anchor =
      tickCount === 1 || (i > 0 && i < tickCount - 1)
        ? "middle"
        : i === 0
          ? "start"
          : "end";
    return { x: xOf(t), label: formatTick(new Date(t)), anchor } as const;
  });

  const first = points[0];
  const last = points[points.length - 1];
  const trend =
    last.mastery > first.mastery
      ? "increased"
      : last.mastery < first.mastery
        ? "decreased"
        : "stayed the same";
  const ariaLabel = `Mastery over time from ${formatTick(
    parseLocalDate(first.date),
  )} to ${formatTick(parseLocalDate(last.date))}: ${trend}, from ${Math.round(
    first.mastery * 100,
  )}% to ${Math.round(last.mastery * 100)}%.`;

  return (
    <div
      className="w-full"
      style={{
        aspectRatio: `${TIMELINE_CHART_VIEW_W} / ${TIMELINE_CHART_VIEW_H}`,
      }}
    >
      <svg
        role="img"
        aria-label={ariaLabel}
        viewBox={`0 0 ${TIMELINE_CHART_VIEW_W} ${TIMELINE_CHART_VIEW_H}`}
        width="100%"
        height="100%"
        className="block"
      >
        {MASTERY_BANDS.map((b) => (
          <rect
            key={b.level}
            x={MARGIN.left}
            y={yOf(b.to)}
            width={PLOT_W}
            height={yOf(b.from) - yOf(b.to)}
            fill={MASTERY_CATEGORIES[b.level].pill}
          />
        ))}

        {/* y-axis endpoints only — the bands carry the meaning in between */}
        <text
          x={MARGIN.left - 5}
          y={MARGIN.top + 4}
          textAnchor="end"
          fontSize="10"
          fill="#9AA0A6"
        >
          1
        </text>
        <text
          x={MARGIN.left - 5}
          y={MARGIN.top + PLOT_H}
          textAnchor="end"
          fontSize="10"
          fill="#9AA0A6"
        >
          0
        </text>

        <path d={path} fill="none" stroke="#02734A" strokeWidth={2} />
        {xs.map((x, i) => (
          <circle
            key={points[i].date}
            cx={x}
            cy={ys[i]}
            r={3}
            fill="#02734A"
            stroke="#fff"
            strokeWidth={1.2}
          />
        ))}

        {ticks.map((t, i) => (
          <g key={i}>
            <line
              x1={t.x}
              y1={MARGIN.top + PLOT_H}
              x2={t.x}
              y2={MARGIN.top + PLOT_H + 3}
              stroke="#C9CDD3"
            />
            <text
              x={t.x}
              y={TIMELINE_CHART_VIEW_H - 4}
              textAnchor={t.anchor}
              fontSize="10"
              fill="#9AA0A6"
            >
              {t.label}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
};

export default MasteryTimelineChart;
